// Map the portal's REPORT_V2 report shape to/from SRMS's ReportTo. SRMS keeps its own
// API (SPF uses it too), so the translation lives here. Pure data mapping — unit-tested.
import { SSP_ONLY_METRICS } from '../../../pages/dashboard/reportbuilder/monetizationform/consts';

// Which backend a report lives in; tagged on every merged-list item so follow-up calls route right.
export const SOURCE = { PPA: 'ppa', SRMS: 'srms' };


// Type badge for a PPA-sourced report: SSP if it uses SSP-only (bid/prebid) metrics — i.e. an un-migrated
// SSP report still on PPA — otherwise Ad Management (FAM).
export const ppaReportType = (metrics) =>
  Array.isArray(metrics) && metrics.some((m) => SSP_ONLY_METRICS.includes(m)) ? 'SSP' : 'FAM';

// Portal dateRange <-> SRMS RelativeDate (only the "days" buckets differ).
const DATE_RANGE_TO_RELATIVE = {
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  LAST_SEVEN_DAYS: 'DAYS_7',
  LAST_THIRTY_DAYS: 'DAYS_30',
  LAST_WEEK: 'LAST_WEEK',
  LAST_MONTH: 'LAST_MONTH',
  CUSTOM: 'CUSTOM',
};
const RELATIVE_TO_DATE_RANGE = Object.fromEntries(
  Object.entries(DATE_RANGE_TO_RELATIVE).map(([range, relative]) => [relative, range])
);

// Portal dimension -> SRMS ReportDimension (same names). Unmapped dimensions are dropped.
const DIM_TO_SRMS = {
  DATE: 'DATE', YEAR: 'YEAR', QUARTER: 'QUARTER', MONTH: 'MONTH', WEEK: 'WEEK',
  SITE: 'SITE', GEO: 'GEO', DEVICE: 'DEVICE', AD_UNIT: 'AD_UNIT',
  PRODUCT_TYPE: 'PRODUCT_TYPE', INTEGRATION: 'INTEGRATION',
  REVENUE_TYPE: 'REVENUE_TYPE',
};
const DIM_TO_PORTAL = Object.fromEntries(
  Object.entries(DIM_TO_SRMS).map(([portal, srms]) => [srms, portal])
);

// Metrics SRMS supports (identical names). FAM-only metrics (RPM, page views, viewability, initial/
// refresh loads) aren't in SSP data, so they're dropped — same as the migration's UNSUPPORTED_METRICS.
const SUPPORTED_METRICS = new Set([
  'NET_REVENUE', 'GROSS_REVENUE', 'TOTAL_IMPRESSIONS', 'AVG_CPM', 'AD_REQUEST',
  'BID_RATE', 'WIN_RATE', 'BID_REQUEST', 'BID_RESPONSE', 'WINNING_BID',
  'GROSS_REVENUE_WITHOUT_DISCREPANCY',
  'VIDEO_VIEWED', 'VIDEO_START', 'VIDEO_Q1', 'VIDEO_Q2', 'VIDEO_Q3', 'VIDEO_COMPLETE', 'VIDEO_CLICK',
]);

const warnDropped = (kind, values) => {
  if (values.length) console.warn(`[SRMS] dropping ${kind} not supported by SRMS: ${values.join(', ')}`);
};

// SRMS sends weekly reports on `sendDate` (Sunday when unset) and monthly ones on the 1st. The portal encodes
// the chosen weekday as the schedule's startDate (next occurrence of that day), so derive sendDate from it the
// same way the PPA->SRMS migration does (PpaReportMapper). Parsed from the 'YYYY-MM-DD' parts on purpose:
// `new Date('YYYY-MM-DD')` is UTC midnight and lands on the previous day in US timezones.
const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
export const deriveSendDate = (frequency, startDate) => {
  if (frequency === 'MONTHLY') return 'FIRST_OF_EVERY_MONTH';
  if (frequency !== 'WEEKLY' || !startDate) return null;
  const [y, m, d] = String(startDate).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return WEEKDAYS[new Date(y, m - 1, d).getDay()];
};

// A report is "scheduled" only with a full delivery window + recipients (mirrors the portal slice's rule).
const deriveIsScheduled = (schedule) =>
  !!(schedule && schedule.startDate && schedule.endDate && schedule.format
    && schedule.recipientEmails && schedule.recipientEmails.length > 0);

// REPORT_V2 -> ReportTo (create/update/preview/generate). SRMS re-pins account/createdBy and translates
// GEO/DEVICE filter values, so those are the portal's native codes here.
export const toReportTo = (payload = {}) => {
  const schedule = payload.schedule || {};
  const filtersObj = payload.filters || {};

  const filters = Object.entries(filtersObj)
    .filter(([dim, vals]) => Array.isArray(vals) && vals.length > 0 && DIM_TO_SRMS[dim])
    // "ALL" = every site in the account = no SITE filter (account scope already limits it)
    .filter(([dim, vals]) => !(dim === 'SITE' && vals.length === 1 && vals[0] === 'ALL'))
    .map(([dim, vals]) => ({ dimension: DIM_TO_SRMS[dim], filterValues: vals, condition: true })); // condition=true = include

  const droppedDims = (payload.dimensions || []).filter((d) => !DIM_TO_SRMS[d]);
  const droppedMetrics = (payload.metrics || []).filter((m) => !SUPPORTED_METRICS.has(m));
  warnDropped('dimensions', droppedDims);
  warnDropped('metrics', droppedMetrics);

  const isCustom = payload.dateRange === 'CUSTOM';

  return {
    reportUUID: payload.reportUUID,
    accountUuid: payload.accountUUID,
    isPpReport: true,
    name: payload.name,
    relativeDate: DATE_RANGE_TO_RELATIVE[payload.dateRange] || 'CUSTOM',
    // frequency/format enum strings are identical on both sides.
    schedule: schedule.frequency || 'ONCE',
    scheduleFrom: schedule.startDate || null,
    scheduleTo: schedule.endDate || null,
    sendDate: deriveSendDate(schedule.frequency, schedule.startDate),
    recipients: schedule.recipientEmails || [],
    createdBy: payload.createdBy || 'publisher-portal', // overwritten by SRMS from the token
    dimensions: (payload.dimensions || []).map((d) => DIM_TO_SRMS[d]).filter(Boolean),
    metrics: (payload.metrics || []).filter((m) => SUPPORTED_METRICS.has(m)),
    filters,
    startDate: isCustom ? (payload.from || null) : null,
    endDate: isCustom ? (payload.to || null) : null,
    reportFormat: schedule.format || 'CSV',
  };
};

// ReportTo -> REPORT_V2 list item. `meta` carries createdAt (ReportTo lacks it); _srmsRaw keeps the exact
// ReportTo so download can resend it verbatim.
export const fromReportTo = (rt = {}, meta = {}) => {
  const filters = {};
  (rt.filters || []).forEach((f) => {
    const portalDim = DIM_TO_PORTAL[f.dimension];
    if (!portalDim) return;
    if (f.condition === false) return; // the portal builder only represents include filters
    filters[portalDim] = f.filterValues || [];
  });
  const hasSiteFilter = Array.isArray(filters.SITE) && filters.SITE.length > 0;
  const schedule = {
    frequency: rt.schedule || 'ONCE',
    startDate: rt.scheduleFrom || null,
    endDate: rt.scheduleTo || null,
    format: rt.reportFormat || 'CSV',
    recipientEmails: Array.from(rt.recipients || []),
  };

  return {
    _source: SOURCE.SRMS,
    _hydrated: true,
    reportProductType: 'SSP',
    _srmsRaw: rt,
    reportUUID: rt.reportUUID,
    accountUUID: rt.accountUuid,
    name: rt.name,
    createdAt: meta.createdAt || rt.createdAt || null,
    dateRange: RELATIVE_TO_DATE_RANGE[rt.relativeDate] || 'CUSTOM',
    from: rt.startDate || null,
    to: rt.endDate || null,
    dimensions: (rt.dimensions || []).map((d) => DIM_TO_PORTAL[d]).filter(Boolean),
    metrics: (rt.metrics || []).filter((m) => SUPPORTED_METRICS.has(m)),
    // no SITE filter = all sites; show the ALL sentinel so the builder repopulates them
    filters: hasSiteFilter ? filters : { ...filters, SITE: ['ALL'] },
    allSitesSelected: !hasSiteFilter,
    schedule,
    // drives the "Scheduled Email Report" toggle; same rule the slice uses for list items
    isScheduled: deriveIsScheduled(schedule),
  };
};

// ReportMetadataTo -> a table-ready but un-hydrated list item: schedule filled, builder fields empty.
// Full config is fetched lazily on edit/download.
export const fromMetadata = (meta = {}) => {
  const schedule = {
    frequency: meta.schedule || 'ONCE',
    startDate: meta.scheduleFrom || null,
    endDate: meta.scheduleTo || null,
    format: 'CSV', // SSP reports are CSV-only
    // placeholder length for the table's count; real list arrives on hydration
    recipientEmails: new Array(meta.recipientCount || 0).fill(''),
  };
  return {
    _source: SOURCE.SRMS,
    _hydrated: false,
    reportProductType: 'SSP',
    reportUUID: meta.uuid,
    accountUUID: meta.accountUuid,
    name: meta.name,
    createdAt: meta.createdAt || null,
    // safe empties until hydrated
    dateRange: 'CUSTOM',
    from: null,
    to: null,
    dimensions: [],
    metrics: [],
    filters: {},
    allSitesSelected: false,
    schedule,
    isScheduled: deriveIsScheduled(schedule),
  };
};
